/**
 * Custom Lexical node for section-info embed elements
*/

import { yupResolver } from '@hookform/resolvers/yup';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from "yup";

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedSectionInfo } from '@dl/declarations';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ControlledSystemTextInput } from '../../data/TextInput';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer, CustomEmbedPluginNote } from './CustomEmbedPluginContainer';

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedSectionInfo;

interface FormatTextProps {
    text: string;
}

const FormatText: React.FC<FormatTextProps> = ({ text }) => {
    /*
     * More than 1 consecutive lines of text are transformed in a list
     * Individual lines are a paragraph
    */

    // split by empty lines
    return text.split(/\n\s*\n/).map((content, i) => {
        // split by new line
        const tmp = content
            .trim()
            .split('\n')
            .map((t) => t.trim());

        // one line is a paragraph
        if (tmp.length === 1) {
            return <p key={`p-${i}-${tmp[0].slice(0, 10)}`}>{tmp[0]}</p>;
        }

        // multiple lines are a list
        return (
            <ul className="list-outside pl-8" key={`ul-${i}-${tmp.length}`}>
                {tmp.map((l, j) => (
                    <li key={`li-${i}-${j}-${l.slice(0, 10)}`}>{l}</li>
                ))}
            </ul>
        );
    });
};

interface EmbedSectionInfoProps {
    config: TBaseType;
}

export const EmbedSectionInfo: React.FC<EmbedSectionInfoProps> = ({ config }) => {
    const { title, text } = (config as TExtendedType).embed.config;

    return (
        <div className="bg-gray-60 p-5 dark:bg-primary-700">
            {title && <div className="text-[1.5rem] font-bold">{title}</div>}
            <div className="gap-y-5">
                <FormatText text={text} />
            </div>
        </div>
    );
};

export const EditEmbedSectionInfo: React.FC<
    EmbedSectionInfoProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm({
        resolver: yupResolver(yup.object().shape({
            title: yup.string().label("Title"),
            text: yup.string().required().label("Text"),
        })),
        defaultValues: {
            title: usedConfig.embed.config.title,
            text: usedConfig.embed.config.text
        }
    })

    const { handleSubmit: handleFormSubmit } = methods;

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {
                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createSectionInfoNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (
        <CustomEmbedPluginContainer
            embedTitle='Embed/Section Info'
            embedDescription={(
                <>
                    <b>Create a bullet list</b> at the beginning with the main topics to discuss in the article.
                </>
            )}
            instructionsScreenshotUrl='/images/embeds/section-info.png'
            instructionsNotesComponent={(
                <div className="w-full">
                    <CustomEmbedPluginNote
                        wrapperClassName='w-full flex items-center gap-8'
                        notes={[
                            {
                                title: "To form a list",
                                description: (
                                    <p>
                                        Add lines of text separated by line breaks. Each individual line constitutes a paragraph.
                                    </p>
                                )
                            }
                        ]}
                    />
                </div>
            )}
            embedActionComponent={(
                <FormProvider {...methods}>
                    <form className="flex flex-col gap-4">
                        <ControlledSystemTextInput
                            name='title'
                            inputSize='full'
                            placeholder="Title"
                        />

                        <ControlledSystemTextInput
                            name='text'
                            inputSize='full'
                            placeholder="Text (multiple consecutive lines of text form a list, individual lines are a paragraph):"
                            className="form-control"
                            id="embed-section-info-text"
                            multiline
                            rows={8}
                        />

                        <CustomEmbedPluginActions
                            handleSubmit={handleSubmitEdits}
                        />
                    </form>
                </FormProvider>
            )}
        />
    )
}

export const EditEmbedSectionInfoDialog: React.FC<
    EmbedSectionInfoProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/Section Info'
            }}
            content={{
                component: (
                    <EditEmbedSectionInfo config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

export class CustomSectionInfoNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed Section Info",
            <EmbedSectionInfo config={config} />,
            <EditEmbedSectionInfo config={config} handleSubmit={(config) => { this.handleSubmit(config) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): string {
        return "section-info"
    }

    static clone(node: CustomSectionInfoNode): CustomSectionInfoNode {
        return new CustomSectionInfoNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomSectionInfoNode {
        return $createSectionInfoNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $createSectionInfoNode(
    config: TBaseType,
): CustomSectionInfoNode {
    return new CustomSectionInfoNode(config);
}

export function $isSectionInfoNode(
    node: CustomSectionInfoNode | LexicalNode | null | undefined,
): node is CustomSectionInfoNode {
    return node instanceof CustomSectionInfoNode;
}
